// ── Card Definitions (JSON shape) ──────────────────────────────────

import type { Target } from './shared/targeting.js';

export type CardType = 'character' | 'action';
export type ActionSubtype = 'action' | 'reaction';
export type AbilityType =
  | 'on-play' | 'passive' | 'activated'
  | 'on-attack' | 'on-attack-alone' | 'on-damage-opponent'
  | 'end-of-turn' | 'beginning-of-turn'
  | 'on-play-character' | 'on-play-action';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface Ability {
  type: AbilityType;
  effect: string;     // When target is set, this is a Verb (see src/shared/targeting.ts).
                      // When target is absent, this is an atomic slug handled by
                      // EFFECT_HANDLERS in src/engine/AbilityProcessor.ts.
  target?: Target;    // Optional — present for split verb+target abilities.
  intensity?: number; // numeric parameter for the effect, e.g. 200 for +200 Power
}

const ABILITY_ICONS: Record<AbilityType, string> = {
  'on-play': '⚡',
  'passive': '🌸',
  'activated': '↩️',
  'on-attack': '⚔️',
  'on-attack-alone': '⚔️',
  'on-damage-opponent': '⚔️',
  'end-of-turn': '🌸',
  'beginning-of-turn': '🌸',
  // 💫 (yellow swirl, paired with ⚡) for the "reaction-to-play" family —
  // these fire AFTER another card's on-play resolves, so the icon reads
  // as the ripple after the deploy moment rather than the deploy itself.
  'on-play-character': '💫',
  'on-play-action': '💫',
};

export function getAbilityIcon(type: AbilityType): string {
  return ABILITY_ICONS[type];
}

export interface CharacterCardDef {
  schema_version: number;  // see src/shared/card-migrations.ts — migrateCard() guarantees this is set.
  id: string;
  name: string;
  description?: string;    // one-sentence character lore (from cardgen candidate phase or flavor fallback).
  type: 'character';
  power: number;
  spirit: number;
  rarity: Rarity;
  tags: string[];
  flavor: string;
  art: string;
  ability?: Ability;
  clock?: number;          // play-clock threshold; defaults to 1 when absent.
}

export interface ActionCardDef {
  schema_version: number;
  id: string;
  name: string;
  description?: string;  // one-sentence card lore.
  type: 'action';
  subtype: ActionSubtype;
  spirit: number;
  rarity: Rarity;
  tags: string[];
  flavor: string;
  art: string;
  effect: string;        // Verb if target is set; atomic slug otherwise.
  target?: Target;       // Optional — present for split verb+target effects.
  intensity?: number;
  clock?: number;        // play-clock threshold; defaults to 1 when absent.
}

export type CardDef = CharacterCardDef | ActionCardDef;

export interface DeckDef {
  name: string;
  cards: { id: string; count: number }[];
}

// ── Runtime State ──────────────────────────────────────────────────

// Re-export PRNGState into types.ts's namespace so the rest of the codebase
// doesn't need to know which engine file defines the PRNG.
export type { PRNGState } from './engine/PRNG.js';
import type { PRNGState } from './engine/PRNG.js';

export type Phase = 'activate' | 'draw' | 'main' | 'attack' | 'counter' | 'end' | 'cleanup';
export type PlayerSide = 'player' | 'opponent';
export type Zone = 'deck' | 'hand' | 'ring' | 'attackLine' | 'discard';

export interface CardInstance {
  instanceId: string;
  cardId: string;
  owner: PlayerSide;
  currentPower: number;
  armor: number;
  isFresh: boolean;
  isExhausted: boolean;
  supports: string[];   // instanceIds of attached support cards
  zone: Zone;
  skipNextRefresh: boolean;  // keep-exhausted: stays exhausted through next activate
  /** True once this instance has spent its ability — currently set by the
   *  passive-rebirth resurrection path: when the character would die, it's
   *  restored to the ring with new power AND this flag prevents its
   *  ability (rebirth or otherwise) from firing again. Read via
   *  `hasPassive`, which gates on this before consulting the CardDef. */
  abilityDisabled: boolean;
  /** Set by `projectForSide()` on instances the active perspective should
   *  not see (the opposite side's hand and deck). When true, `cardId` is
   *  replaced with the UNKNOWN_CARD_ID sentinel and power/armor are zeroed.
   *  Any attempt to look up the def via getCardDef will throw, which is
   *  the safety net against accidental info leaks. Never set on the
   *  canonical live state — only on projected clones. */
  isUnknown?: boolean;
}

export interface RingSlot {
  instanceId: string | null;
}

export interface PlayerState {
  hp: number;
  spirit: number;
  deck: string[];          // instanceIds, index 0 = top
  hand: string[];          // instanceIds
  ring: RingSlot[];        // fixed-length array (config.ringSlots)
  discardPile: string[];   // instanceIds
  attackLine: string[];    // instanceIds committed to attack
  ralliesUsed: number;
  supportsUsed: number;
  bonusRallies: number;
  bonusSupports: number;
  /** Snapshot of `hp` and `deck.length` at the start of this player's
   *  most recent activate phase. `lastTurnHp/DeckDelta` are computed
   *  from these at each activate. The deltas drive SearchAI's
   *  doom-stall detector: a negative delta projects forward to estimate
   *  when this player would lose if neither side acted. Cheap to track
   *  and only consulted by the AI's decision logic. */
  lastActivateHp: number;
  lastActivateDeck: number;
  /** HP / deck-size change since the previous activate phase. Negative
   *  = this player is bleeding HP / drawing down the deck at that rate
   *  per turn cycle. Initialized to 0 and updated at each activate. */
  lastTurnHpDelta: number;
  lastTurnDeckDelta: number;
  /** True if this player dispatched DECLARE_ATTACKERS during their
   *  current turn. Reset at the start of their next activate. Read by
   *  the AI's doom-stall detector to confirm that the opponent isn't
   *  applying combat pressure (one of the stall preconditions). */
  attackedDuringTurn: boolean;
}

export interface AttackGroup {
  attackerIds: string[];   // 1 for solo, 2-3 for united
  united: boolean;
}

export interface CombatState {
  groups: AttackGroup[];
  blockerAssignments: Map<number, string>; // groupIndex → blockerInstanceId
}

export interface GameState {
  turnNumber: number;
  currentPlayer: PlayerSide;
  /** Set once at game start (from rule.startingPlayer or a random coin flip)
   *  and never mutated. Drives the "first player skips draw on their first
   *  turn" rule and the "clock advances at start of second player's turn"
   *  rule, both of which used to assume the player always went first. */
  firstPlayer: PlayerSide;
  phase: Phase;
  isFirstTurn: boolean;
  /** Mirror of the active GameRule's `firstPlayerDrawsOnFirstTurn`,
   *  defaulting to true when the rule omits it. When true (the
   *  default), the engine does NOT skip the first player's first-turn
   *  draw. Stashed on state so TurnManager doesn't need to take the
   *  rule by parameter. */
  firstPlayerDrawsOnFirstTurn: boolean;
  clock: number;          // shared play-clock; advances at start of every "second player" (non-firstPlayer) turn.
  /** Upper bound the play-clock wraps around: at `maxClock`, +1 brings
   *  it back to 1. Historical default is 12; rapid-cycle tournaments
   *  cap lower (e.g. 3). Mirrored from `rule.maxClock` at game start
   *  so `adjustClock` doesn't need the rule passed in. */
  maxClock: number;
  /** HP cap for this game. Used as the bar denominator and the heal ceiling.
   *  Comes from the active GameRule's `maxHp` (falls back to config). */
  maxHp: number;
  player: PlayerState;
  opponent: PlayerState;
  cardInstances: Map<string, CardInstance>;
  combatState: CombatState | null;
  winner: PlayerSide | null;
  winReason: 'hp' | 'deckout' | null;
  log: string[];
  /** Once-per-match latches for the opponent voice triggers
   *  (`opponent_surprised` / `opponent_gloat`). Set the first time each
   *  trigger fires so subsequent matches of the firing condition stay
   *  silent. Lives on game state so it survives the engine→UI bridge
   *  without an extra side channel. */
  opponentSurprisedFired?: boolean;
  opponentGloatFired?: boolean;
  /** Seedable PRNG threaded through every engine operation that needs
   *  randomness (deck shuffle, coin flip, random discards, AI-internal
   *  rolls). Mutated in place during `dispatch`. Cloned by `cloneState`,
   *  so simulations branching off a state get independent streams. */
  rng: PRNGState;
  /** Monotonic counter for fresh CardInstance IDs. Previously a module-
   *  global in DeckUtils; lives on state so concurrent simulations (AI
   *  tree search) don't collide on IDs. Bumped by createInstanceId(state). */
  nextInstanceId: number;
}

// ── Actions ────────────────────────────────────────────────────────

export type GameAction =
  | { type: 'PLAY_CHARACTER'; instanceId: string; slotIndex: number }
  | { type: 'SUPPORT_CHARACTER'; handInstanceId: string; ringInstanceId: string }
  | { type: 'ACTIVATE_ABILITY'; instanceId: string }
  | { type: 'PLAY_ACTION'; instanceId: string }
  | { type: 'DECLARE_ATTACKERS'; groups: AttackGroup[] }
  | { type: 'ASSIGN_BLOCKER'; groupIndex: number; blockerInstanceId: string }
  | { type: 'UNASSIGN_BLOCKER'; blockerInstanceId: string }
  | { type: 'PLAY_REACTION'; instanceId: string }
  | { type: 'CONFIRM_BLOCKERS' }
  | { type: 'DISCARD_CARD'; instanceId: string }
  | { type: 'SKIP_PHASE' }
  | { type: 'ADVANCE_PHASE' };

// ── Events (engine → UI) ──────────────────────────────────────────

export type GameEvent =
  | { type: 'phase-changed'; phase: Phase; player: PlayerSide }
  | { type: 'clock-changed'; clock: number }
  | { type: 'card-drawn'; instanceId: string; player: PlayerSide }
  | { type: 'card-played'; instanceId: string; slotIndex: number; player: PlayerSide }
  | { type: 'support-attached'; handInstanceId: string; ringInstanceId: string; player: PlayerSide }
  | { type: 'ability-triggered'; instanceId: string; abilityType: AbilityType; effect: string }
  | { type: 'action-played'; instanceId: string; player: PlayerSide; effect: string }
  | { type: 'attack-declared'; groups: AttackGroup[]; player: PlayerSide }
  | { type: 'blocker-assigned'; groupIndex: number; blockerInstanceId: string }
  | { type: 'combat-damage'; attackerInstanceId: string; defenderInstanceId: string | null; damage: number;
      /** Set true on the lead attacker's outgoing damage event when
       *  the source is a united attack (resolveUnitedGroup). Lets the
       *  voice-line router identify the canonical "primary attacker"
       *  event (a blocked united emits multiple counter-damage events
       *  from the blocker first, so picking the first combat-damage
       *  event would otherwise misidentify the blocker as the
       *  attacker). Also flips the combat-prob gate to the higher
       *  `voiceLineUnitedCombatProb`. */
      united?: boolean }
  | { type: 'hp-changed'; player: PlayerSide; oldHp: number; newHp: number; amount: number }
  | { type: 'spirit-changed'; player: PlayerSide; oldSpirit: number; newSpirit: number }
  | { type: 'character-ko'; instanceId: string; player: PlayerSide }
  | { type: 'character-refreshed'; instanceId: string }
  | { type: 'character-exhausted'; instanceId: string }
  | { type: 'character-locked'; instanceId: string }
  | { type: 'ability-silenced'; instanceId: string }
  | { type: 'power-changed'; instanceId: string; oldPower: number; newPower: number; amount: number }
  | { type: 'armor-changed'; instanceId: string; oldArmor: number; newArmor: number; amount: number }
  | { type: 'card-returned'; instanceId: string; player: PlayerSide }
  | { type: 'card-milled'; instanceId: string; player: PlayerSide }
  | { type: 'card-discarded'; instanceId: string; player: PlayerSide }
  // Voice-line routing: emitted just before `koCharacter` whenever a
  // character's damage causes a KO. The UI uses this to route the
  // `kill_other` voice trigger to the killer rather than the victim.
  | { type: 'kill-credit'; killerInstanceId: string; victimInstanceId: string }
  // Turn-boundary state-snapshot triggers for opponent voice lines.
  // Fired at most once per match per kind (TurnManager owns the flags).
  | { type: 'opponent-surprised' }
  | { type: 'opponent-gloat' }
  // `responsibleCardId` is the card that delivered the final blow —
  // a character `cardId` if combat caused HP→0, an action-card or
  // ability source `cardId` if a verb/ability finished the opponent,
  // or `null` for deck-out (no responsible card). Consumed by the
  // end-game interstitial to pick which card slides in for the
  // kill_opponent scene.
  | { type: 'game-over'; winner: PlayerSide; reason: 'hp' | 'deckout'; responsibleCardId: string | null };

export interface DispatchResult {
  state: GameState;
  events: GameEvent[];
}
